import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { usePermission } from '@/hooks/use-permission';
import { ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useCreatePayment } from '@/hooks/use-create-payment';

interface FormData {
  bank: string;
  amountDollars: string;
  billReference: string;
  merchantName: string;
  expiresInMinutes: string;
}

interface FormErrors {
  bank?: string;
  amountDollars?: string;
  merchantName?: string;
  expiresInMinutes?: string;
}

function FormField({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {hint && !error && <p className="text-xs text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function validateForm(data: FormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.bank) {
    errors.bank = 'Bank is required';
  }

  const amount = parseFloat(data.amountDollars);
  if (!data.amountDollars) {
    errors.amountDollars = 'Amount is required';
  } else if (isNaN(amount) || amount <= 0) {
    errors.amountDollars = 'Amount must be greater than 0';
  } else if (amount > 999999.99) {
    errors.amountDollars = 'Amount exceeds maximum allowed';
  }

  if (!data.merchantName.trim()) {
    errors.merchantName = 'Merchant name is required';
  }

  const minutes = parseInt(data.expiresInMinutes);
  if (!data.expiresInMinutes) {
    errors.expiresInMinutes = 'Expiry is required';
  } else if (isNaN(minutes) || minutes < 1) {
    errors.expiresInMinutes = 'Expiry must be at least 1 minute';
  } else if (minutes > 1440) {
    errors.expiresInMinutes = 'Expiry cannot exceed 1440 minutes (24h)';
  }

  return errors;
}

export function CreatePaymentPage() {
  const canCreate = usePermission('paynow:payments:create');
  const navigate = useNavigate();
  const createMutation = useCreatePayment();

  const [form, setForm] = useState<FormData>({
    bank: 'DBS',
    amountDollars: '',
    billReference: '',
    merchantName: 'Keppel',
    expiresInMinutes: '30',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  if (!canCreate) return <Navigate to="/" replace />;

  function handleChange(field: keyof FormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const amountCents = Math.round(parseFloat(form.amountDollars) * 100);

    createMutation.mutate(
      {
        bank: form.bank,
        amountCents,
        billReference: form.billReference.trim() || undefined,
        merchantName: form.merchantName.trim(),
        expiresInMinutes: parseInt(form.expiresInMinutes),
      },
      {
        onSuccess: (payment) => {
          navigate(`/payments/${payment.id}`);
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/payments')}>
          <ArrowLeft className="h-4 w-4" /> Back to Payments
        </Button>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900">Create Payment</h2>
        <p className="mt-1 text-sm text-slate-500">Generate a new PayNow QR payment request</p>
      </div>

      <div className="max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Payment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <FormField label="Bank" error={errors.bank}>
                <Select
                  value={form.bank}
                  onChange={(e) => handleChange('bank', e.target.value)}
                  aria-invalid={!!errors.bank}
                >
                  <option value="DBS">DBS</option>
                  <option value="UOB">UOB</option>
                </Select>
              </FormField>

              <FormField
                label="Amount (SGD)"
                error={errors.amountDollars}
                hint="Enter amount in dollars, e.g. 100.00"
              >
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={form.amountDollars}
                  onChange={(e) => handleChange('amountDollars', e.target.value)}
                  aria-invalid={!!errors.amountDollars}
                />
              </FormField>

              <FormField label="Bill Reference" hint="Optional — auto-generated if left empty">
                <Input
                  type="text"
                  placeholder="e.g. INV-2026-001"
                  value={form.billReference}
                  onChange={(e) => handleChange('billReference', e.target.value)}
                  maxLength={35}
                />
              </FormField>

              <FormField label="Merchant Name" error={errors.merchantName}>
                <Input
                  type="text"
                  value={form.merchantName}
                  onChange={(e) => handleChange('merchantName', e.target.value)}
                  aria-invalid={!!errors.merchantName}
                  maxLength={50}
                />
              </FormField>

              <FormField
                label="Expiry (minutes)"
                error={errors.expiresInMinutes}
                hint="How long the QR code remains valid (1–1440 min)"
              >
                <Input
                  type="number"
                  min="1"
                  max="1440"
                  value={form.expiresInMinutes}
                  onChange={(e) => handleChange('expiresInMinutes', e.target.value)}
                  aria-invalid={!!errors.expiresInMinutes}
                />
              </FormField>

              {createMutation.error && (
                <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                  {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create payment'}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/payments')}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending} className="flex-1">
                  {createMutation.isPending ? 'Creating...' : 'Create Payment'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
